import React, { useState, useMemo } from 'react';
import { formatCurrency, formatPercentage } from '../../utils/formatters';
import { RISK_CATEGORIES, EXTREME_EVENTS } from '../../constants/extremeEventsConfig';

/**
 * Risk Heatmap Visualization Component
 * Phase 2: Interactive heatmap showing risk categories, severity, and frequency
 */
const RiskHeatmapSubtab = ({ 
    simulationResults,
    isDarkMode = false 
}) => {
    const [heatmapView, setHeatmapView] = useState('frequency'); // 'frequency', 'impact', 'combined'
    const [selectedCell, setSelectedCell] = useState(null);

    // Generate heatmap data
    const heatmapData = useMemo(() => {
        if (!simulationResults?.extremeEventResults) return null;

        const categories = Object.keys(RISK_CATEGORIES);
        const severityLevels = ['Low', 'Medium', 'High', 'Critical', 'Catastrophic'];
        
        // Initialize heatmap matrix
        const matrix = {};
        categories.forEach(categoryId => {
            matrix[categoryId] = {};
            severityLevels.forEach(severity => {
                matrix[categoryId][severity] = {
                    count: 0,
                    totalImpact: 0,
                    averageImpact: 0,
                    totalNPVLoss: 0,
                    events: [],
                    maxImpact: 0,
                    minImpact: 1
                };
            });
        });

        // Process extreme event results
        simulationResults.extremeEventResults.forEach(result => {
            const event = result.extremeEvent;
            const categoryId = event.categoryId;
            const impact = 1 - event.impactMultiplier;
            const npvLoss = result.originalResults.projectNPV - result.impactedResults.projectNPV;
            
            // Determine severity level based on impact
            let severity;
            if (impact < 0.1) severity = 'Low';
            else if (impact < 0.3) severity = 'Medium';
            else if (impact < 0.6) severity = 'High';
            else if (impact < 0.8) severity = 'Critical';
            else severity = 'Catastrophic';

            if (matrix[categoryId] && matrix[categoryId][severity]) {
                const cell = matrix[categoryId][severity];
                cell.count++;
                cell.totalImpact += impact;
                cell.totalNPVLoss += npvLoss;
                cell.events.push({
                    name: event.event.name,
                    impact,
                    npvLoss,
                    scenario: result.scenario
                });
                cell.maxImpact = Math.max(cell.maxImpact, impact);
                cell.minImpact = Math.min(cell.minImpact, impact);
                cell.averageImpact = cell.totalImpact / cell.count;
            }
        });

        // Calculate normalization values for color scaling
        let maxCount = 0;
        let maxAvgImpact = 0;
        let maxNPVLoss = 0;

        categories.forEach(categoryId => {
            severityLevels.forEach(severity => {
                const cell = matrix[categoryId][severity];
                maxCount = Math.max(maxCount, cell.count);
                maxAvgImpact = Math.max(maxAvgImpact, cell.averageImpact);
                maxNPVLoss = Math.max(maxNPVLoss, Math.abs(cell.totalNPVLoss));
            });
        });

        return {
            matrix,
            categories,
            severityLevels,
            maxCount,
            maxAvgImpact,
            maxNPVLoss
        };
    }, [simulationResults]);

    const getCellColor = (cell, maxValue) => {
        if (!cell || cell.count === 0) return 'bg-gray-100 dark:bg-gray-800';
        
        let intensity;
        switch (heatmapView) {
            case 'frequency':
                intensity = cell.count / maxValue;
                break;
            case 'impact':
                intensity = cell.averageImpact / maxValue;
                break;
            case 'combined':
                intensity = (cell.count / heatmapData.maxCount * 0.5) + (cell.averageImpact / heatmapData.maxAvgImpact * 0.5);
                break;
            default:
                intensity = 0;
        }

        // Generate color based on intensity (0-1)
        if (intensity < 0.2) return 'bg-green-100 dark:bg-green-900/20';
        else if (intensity < 0.4) return 'bg-yellow-200 dark:bg-yellow-900/30';
        else if (intensity < 0.6) return 'bg-orange-300 dark:bg-orange-900/40';
        else if (intensity < 0.8) return 'bg-red-400 dark:bg-red-900/60';
        else return 'bg-red-600 dark:bg-red-900/80';
    };

    const getCellTextColor = (cell, maxValue) => {
        if (!cell || cell.count === 0) return 'text-gray-500 dark:text-gray-400';
        
        let intensity;
        switch (heatmapView) {
            case 'frequency':
                intensity = cell.count / maxValue;
                break;
            case 'impact':
                intensity = cell.averageImpact / maxValue;
                break;
            case 'combined':
                intensity = (cell.count / heatmapData.maxCount * 0.5) + (cell.averageImpact / heatmapData.maxAvgImpact * 0.5);
                break;
            default:
                intensity = 0;
        }

        return intensity > 0.6 ? 'text-white' : 'text-gray-800 dark:text-gray-200';
    };

    const getCellValue = (cell) => {
        switch (heatmapView) {
            case 'frequency':
                return cell.count.toString();
            case 'impact':
                return cell.count > 0 ? formatPercentage(cell.averageImpact * 100) : '-';
            case 'combined':
                return cell.count > 0 ? `${cell.count} (${formatPercentage(cell.averageImpact * 100)})` : '-';
            default:
                return '-';
        }
    };

    const getMaxValue = () => {
        switch (heatmapView) {
            case 'frequency':
                return heatmapData?.maxCount || 1;
            case 'impact':
                return heatmapData?.maxAvgImpact || 1;
            case 'combined':
                return 1; // Normalized combined value
            default:
                return 1;
        }
    };

    const getCategoryDisplayName = (categoryId) => {
        const names = {
            FINANCIAL_RISK: '🏦 Financial',
            STRATEGIC_RISK: '📈 Strategic', 
            HAZARD_RISK: '⚡ Hazard',
            OPERATIONS_RISK: '⚙️ Operations',
            EXTREME_BLACK_SWAN: '🌋 Black Swan'
        };
        return names[categoryId] || categoryId;
    };

    if (!heatmapData) {
        return (
            <div className="text-center p-8">
                <div className="text-4xl mb-4">🔥</div>
                <h3 className="text-lg font-semibold mb-2">No Heatmap Data</h3>
                <p className="text-gray-600 dark:text-gray-400">
                    Run an extreme events simulation to see risk category heatmaps.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">View:</span>
                    <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        {[
                            { key: 'frequency', label: 'Frequency', description: 'Event count' },
                            { key: 'impact', label: 'Impact', description: 'Average impact severity' },
                            { key: 'combined', label: 'Combined', description: 'Frequency × Impact' }
                        ].map(view => (
                            <button
                                key={view.key}
                                onClick={() => setHeatmapView(view.key)}
                                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                    heatmapView === view.key
                                        ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                        : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                                }`}
                                title={view.description}
                            >
                                {view.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex items-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-green-100 dark:bg-green-900/20 rounded"></div>
                        <span>Low</span>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-yellow-200 dark:bg-yellow-900/30 rounded"></div>
                        <span>Medium</span>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-orange-300 dark:bg-orange-900/40 rounded"></div>
                        <span>High</span>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-red-400 dark:bg-red-900/60 rounded"></div>
                        <span>Critical</span>
                    </div>
                    <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 bg-red-600 dark:bg-red-900/80 rounded"></div>
                        <span>Extreme</span>
                    </div>
                </div>
            </div>

            {/* Heatmap */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Risk Category × Severity Heatmap
                </h3>
                
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr>
                                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                                    Category / Severity
                                </th>
                                {heatmapData.severityLevels.map(severity => (
                                    <th key={severity} className="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                                        {severity}
                                    </th>
                                ))}
                                <th className="px-4 py-2 text-center text-sm font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                                    Total
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {heatmapData.categories.map(categoryId => (
                                <tr key={categoryId}>
                                    <td className="px-4 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700">
                                        {getCategoryDisplayName(categoryId)}
                                    </td>
                                    {heatmapData.severityLevels.map(severity => {
                                        const cell = heatmapData.matrix[categoryId][severity];
                                        const maxValue = getMaxValue();
                                        return (
                                            <td 
                                                key={severity}
                                                className={`px-4 py-2 text-center text-sm border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-all hover:shadow-lg ${
                                                    getCellColor(cell, maxValue)
                                                } ${getCellTextColor(cell, maxValue)}`}
                                                onClick={() => setSelectedCell(cell.count > 0 ? { categoryId, severity, cell } : null)}
                                                title={cell.count > 0 ? `${cell.count} events, ${formatPercentage(cell.averageImpact * 100)} avg impact` : 'No events'}
                                            >
                                                {getCellValue(cell)}
                                            </td>
                                        );
                                    })}
                                    <td className="px-4 py-2 text-center text-sm font-semibold text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-700">
                                        {Object.values(heatmapData.matrix[categoryId]).reduce((sum, cell) => sum + cell.count, 0)}
                                    </td>
                                </tr>
                            ))}
                            {/* Totals row */}
                            <tr className="bg-gray-50 dark:bg-gray-700/50">
                                <td className="px-4 py-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                                    Total
                                </td>
                                {heatmapData.severityLevels.map(severity => (
                                    <td key={severity} className="px-4 py-2 text-center text-sm font-semibold text-gray-800 dark:text-gray-200">
                                        {heatmapData.categories.reduce((sum, categoryId) => 
                                            sum + heatmapData.matrix[categoryId][severity].count, 0
                                        )}
                                    </td>
                                ))}
                                <td className="px-4 py-2 text-center text-sm font-bold text-gray-800 dark:text-gray-200">
                                    {simulationResults.extremeEventResults.length}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Selected Cell Details */}
            {selectedCell && (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        {getCategoryDisplayName(selectedCell.categoryId)} - {selectedCell.severity} Severity Events
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Event Count</div>
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                {selectedCell.cell.count}
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Average Impact</div>
                            <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                                {formatPercentage(selectedCell.cell.averageImpact * 100)}
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded">
                            <div className="text-sm text-gray-600 dark:text-gray-400">Total NPV Loss</div>
                            <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                                {formatCurrency(selectedCell.cell.totalNPVLoss)}
                            </div>
                        </div>
                    </div>

                    {selectedCell.cell.events.length > 0 && (
                        <div>
                            <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Event Details</h4>
                            <div className="max-h-40 overflow-y-auto space-y-2">
                                {selectedCell.cell.events.slice(0, 10).map((event, index) => (
                                    <div key={index} className="flex justify-between items-center py-1 px-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                                        <span className="text-gray-800 dark:text-gray-200">{event.name}</span>
                                        <div className="flex space-x-4 text-xs">
                                            <span className="text-orange-600 dark:text-orange-400">
                                                {formatPercentage(event.impact * 100)}
                                            </span>
                                            <span className="text-red-600 dark:text-red-400">
                                                {formatCurrency(event.npvLoss)}
                                            </span>
                                            <span className="text-gray-500 dark:text-gray-400">
                                                #{event.scenario}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {selectedCell.cell.events.length > 10 && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                                        ... and {selectedCell.cell.events.length - 10} more events
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    
                    <button 
                        onClick={() => setSelectedCell(null)}
                        className="mt-4 px-4 py-2 text-sm bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                    >
                        Close Details
                    </button>
                </div>
            )}
        </div>
    );
};

export default RiskHeatmapSubtab;